import { LIMITS, type ChatMessage, type ClientIntent } from '@tctp/protocol';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useShortcuts } from '../useShortcuts';

const hhmm = (t: number): string => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** Bottom-left chat strip (spec §7). With `onClose` it is the portrait chat sheet (§12): every message, and a ✕ instead of the ⌃ toggle. */
export function ChatStrip({ chat, canChat, act, shortcutsEnabled, onClose }: {
  chat: ChatMessage[]; canChat: boolean; act: (intent: ClientIntent) => Promise<void>; shortcutsEnabled: boolean; onClose?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const shown = expanded || onClose ? chat : chat.slice(-2);
  useEffect(() => { list.current?.scrollTo({ top: list.current.scrollHeight }); }, [chat.at(-1)?.id, expanded]);
  const shortcuts = useMemo(() => ({ m: () => input.current?.focus() }), []);
  useShortcuts(shortcuts, shortcutsEnabled);
  function onKey(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key !== 'Enter') return;
    const clean = text.trim();
    if (clean.length === 0) return;
    void act({ type: 'chat', text: clean });
    setText('');
    onClose?.();   // the phone sheet closes on send, so the table comes back at once
  }
  return (
    <div className={`chat-strip${expanded ? ' expanded' : ''}`}>
      <div className="chat-list" ref={list}>
        {shown.length === 0 && <div className="chat-empty">{onClose ? "It's a bit quiet here. Say hi!" : "It's a bit quiet here. Press M and send a message!"}</div>}
        {shown.map(m => <div key={m.id} className="chat-msg"><span className="who">{m.nickname}</span><span className="text">{m.text}</span><span className="when">{hhmm(m.t)}</span></div>)}
      </div>
      {onClose
        ? <button type="button" className="chat-toggle" aria-label="Close chat" onClick={onClose}>✕</button>
        : <button type="button" className="chat-toggle" aria-label={expanded ? 'Show recent messages' : 'Show all messages'} onClick={() => setExpanded(e => !e)}>{expanded ? '⌄' : '⌃'}</button>}
      <div className="chat-input">
        <span className="hint" aria-hidden="true">M</span>
        <input ref={input} aria-label="Message" value={text} maxLength={LIMITS.chatMax} disabled={!canChat}
          placeholder={canChat ? 'Send a message' : 'Take a seat to chat'} onChange={e => setText(e.target.value)} onKeyDown={onKey} />
      </div>
    </div>
  );
}
